import { gql, useSubscription } from "@apollo/client";
import { SparklesIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import {
  AutopilotFilterCreationTypeEnum,
  AutopilotSearchDesignInitializationStep_JobFragment,
  AutopilotSearchDesignStep,
  AutopilotSearchStrategyJobEnum,
  CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStepFragment,
  CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStepFragmentDoc,
  CuratedSearchDesignStepsContainer_CreateAutopilotFiltersSubscription,
  CuratedSearchDesignStepsContainer_CreateAutopilotFiltersSubscriptionVariables,
  CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
  InternalCuratedSearchFilters,
  LinkedinSalesNavigatorFilters,
} from "../../../__generated__/graphql";
import React, { useContext, useEffect, useState } from "react";
import CuratedSearchDesignTextOnlyStep, {
  CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep,
} from "./CuratedSearchDesignStep/CuratedSearchDesignTextOnlyStep";
import {
  useFragment as getFragmentData,
  makeFragmentData,
} from "../../../__generated__";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import CuratedSearchDesignSelectedStrategyCard, {
  CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStep,
} from "./CuratedSearchDesignSelectedStrategyCard";
import CuratedSearchDesignJobGroup, {
  AutopilotSearchDesignInitializationStep_Job,
} from "./CuratedSearchDesignJobGroup";
import CuratedSearchDesignLsnSteps from "./CuratedSearchDesignLsnSteps";
import CuratedLinkedinSalesNavigatorSearchFiltersCard from "../CuratedSearchFilters/CuratedLinkedinSalesNavigatorSearchFiltersCard";
import { AutopilotCuratedLinkedinSalesNavigatorSearchContext_LinkedinSalesNavigatorFilters } from "../../../contexts/AutopilotCuratedLinkedinSalesNavigatorSearchContext";
import CiroExpandableCard from "../../shared/CiroExpandableCard";
import CuratedSearchCardHeader from "../CuratedSearchCard/CuratedSearchCardHeader";
import CuratedSearchFiltersHeaderDisplay from "../CuratedSearchFiltersHeaderDisplay";

export const CuratedSearchDesignStepsContainer_InitializationStep = gql`
  fragment CuratedSearchDesignStepsContainer_InitializationStep on AutopilotSearchDesignInitializationStep {
    initializationData {
      jobs {
        ...AutopilotSearchDesignInitializationStep_Job
      }
    }
  }
  ${AutopilotSearchDesignInitializationStep_Job}
`;

export const CuratedSearchDesignStepsContainer_CreateAutopilotFilters = gql`
  subscription CuratedSearchDesignStepsContainer_CreateAutopilotFilters(
    $input: CreateAutopilotFiltersInput!
  ) {
    createAutopilotFilters(input: $input) {
      ... on AutopilotSearchDesignInitializationStep {
        id
        ...CuratedSearchDesignStepsContainer_InitializationStep
      }
      ... on AutopilotSearchDesignTextOnlyStep {
        id
        ...CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep
        jobId
      }
      ... on AutopilotSearchDesignInternalFiltersStep {
        id
        ...CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStep
        jobId
      }
      ... on AutopilotSearchDesignLsnFiltersStep {
        id
        lsnFiltersData {
          filters {
            ...AutopilotCuratedLinkedinSalesNavigatorSearchContext_LinkedinSalesNavigatorFilters
          }
        }
      }
    }
  }

  ${CuratedSearchDesignStepsContainer_InitializationStep}
  ${CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep}
  ${CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStep}
  ${AutopilotCuratedLinkedinSalesNavigatorSearchContext_LinkedinSalesNavigatorFilters}
`;

interface ICuratedSearchDesignStepsContainer<
  T extends InternalCuratedSearchFilters | LinkedinSalesNavigatorFilters,
> {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  searchType: AutopilotFilterCreationTypeEnum;
  isLsn?: boolean;
  savedFilters: T | null;
  setSavedFilters: (value: T | null) => void;
  selectedStrategy: AutopilotSearchStrategyJobEnum | null;
  setSelectedStrategy: (
    strategy: AutopilotSearchStrategyJobEnum | null,
  ) => void;
  remixContext?: {
    remixPrompt?: string;
    previousSearchFilters?: InternalCuratedSearchFilters;
  };
  showPlanningStrategy: boolean;
  isRemixStep: boolean;
}

const CuratedSearchDesignStepsContainer = <
  T extends InternalCuratedSearchFilters | LinkedinSalesNavigatorFilters,
>({
  isOpen,
  setIsOpen,
  searchType,
  isLsn = false,
  savedFilters,
  setSavedFilters,
  selectedStrategy,
  setSelectedStrategy,
  remixContext,
  showPlanningStrategy,
  isRemixStep,
}: ICuratedSearchDesignStepsContainer<T>) => {
  const { selectedPrompt, specificCompanies } = useContext(
    AutopilotCuratedSearchContext,
  );

  const [allStrategies, setAllStrategies] = useState<
    Record<
      string,
      CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStepFragment
    >
  >({});

  const [bestStrategyFilterId, setBestStrategyFilterId] =
    useState<AutopilotSearchStrategyJobEnum | null>(null);

  const [allJobsAreComplete, setAllJobsAreComplete] = useState(
    Boolean(savedFilters),
  );
  const [hasError, setHasError] = useState(false);
  const [processedSteps, setProcessedSteps] = useState<
    { step: AutopilotSearchDesignStep; beforeShowingStrategy: boolean }[]
  >([]);
  const [jobs, setJobs] = useState<
    AutopilotSearchDesignInitializationStep_JobFragment[]
  >([]);

  // This is used to retry the subscription if it fails, using the state in setRetries
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [retries, setRetries] = useState(0);

  const {
    data: createAutopilotFiltersData,
    error: createAutopilotFiltersError,
    restart: restartCreateAutopilotFilters,
  } = useSubscription<
    CuratedSearchDesignStepsContainer_CreateAutopilotFiltersSubscription,
    CuratedSearchDesignStepsContainer_CreateAutopilotFiltersSubscriptionVariables
  >(CuratedSearchDesignStepsContainer_CreateAutopilotFilters, {
    variables: {
      input: {
        type: searchType,
        prompt: selectedPrompt,
        specificCompanies: specificCompanies?.length
          ? specificCompanies
          : undefined,
        ...(remixContext ? { remixContext } : {}),
      },
    },
    skip: allJobsAreComplete,
    onComplete: () => {
      setAllJobsAreComplete(true);
    },
  });

  useEffect(() => {
    const currentStep =
      createAutopilotFiltersData?.createAutopilotFilters as AutopilotSearchDesignStep;

    if (!currentStep) {
      return;
    }

    if (currentStep.__typename === "AutopilotSearchDesignInitializationStep") {
      if (currentStep.initializationData?.jobs) {
        setJobs(currentStep.initializationData.jobs);
      }
    } else if (
      currentStep.__typename === "AutopilotSearchDesignInternalFiltersStep"
    ) {
      const strategyData = getFragmentData(
        CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStepFragmentDoc,
        makeFragmentData(
          currentStep,
          CuratedSearchDesignSelectedStrategyCard_AutopilotSearchDesignInternalFiltersStepFragmentDoc,
        ),
      );

      // Check if jobId exists before using it as a key or for setting state
      if (currentStep.jobId) {
        const jobId: string = currentStep.jobId; // Explicitly typed variable

        // Add this strategy using the guaranteed string variable
        setAllStrategies((prev) => ({
          ...prev,
          [jobId]: strategyData,
        }));

        // If it's the best strategy, update relevant state
        if (currentStep.internalFiltersData?.isBestStrategy) {
          setBestStrategyFilterId(strategyData.jobId ?? null);
          setSelectedStrategy(jobId as AutopilotSearchStrategyJobEnum); // Use the string variable here too
          setSavedFilters(strategyData.internalFiltersData.filters as T);
        }
      }
    } else if (
      currentStep.__typename === "AutopilotSearchDesignLsnFiltersStep"
    ) {
      setSavedFilters(currentStep.lsnFiltersData.filters as T);
    }

    // Always add the step to processed steps
    setProcessedSteps((previous) => [
      ...previous,
      { step: currentStep, beforeShowingStrategy: !showPlanningStrategy },
    ]);
  }, [
    createAutopilotFiltersData,
    setSavedFilters,
    setAllStrategies,
    setSelectedStrategy,
    showPlanningStrategy,
  ]);

  useEffect(() => {
    if (!createAutopilotFiltersError) {
      return;
    }

    setRetries((prevRetries) => {
      if (prevRetries < 2) {
        restartCreateAutopilotFilters();
        return prevRetries + 1;
      }

      setAllJobsAreComplete(true);
      setHasError(true);
      console.error(
        "createAutopilotFiltersSubscription Error",
        createAutopilotFiltersError,
      );

      return prevRetries;
    });
  }, [createAutopilotFiltersError, restartCreateAutopilotFilters]);

  useEffect(() => {
    // Reset state if selected strategy is reset, used in remix
    if (!savedFilters) {
      setJobs([]);
      setAllStrategies({});
      setBestStrategyFilterId(null);
      setAllJobsAreComplete(false);
      setProcessedSteps([]);
    }
  }, [savedFilters]);

  const bestStrategyRef = React.useRef<HTMLDivElement>(null);

  // Effect to scroll to selected strategy when complete
  useEffect(() => {
    if (
      allJobsAreComplete &&
      selectedStrategy &&
      allStrategies[selectedStrategy]
    ) {
      const shouldScroll =
        (Object.keys(allStrategies).length > 0 &&
          Object.keys(allStrategies).length ===
            jobs.filter((job) => job.id in allStrategies).length) ||
        selectedStrategy !== bestStrategyFilterId;

      if (shouldScroll) {
        setTimeout(() => {
          bestStrategyRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 300);
      }
    }
  }, [
    allJobsAreComplete,
    selectedStrategy,
    allStrategies,
    jobs,
    bestStrategyFilterId,
  ]);

  if (!showPlanningStrategy) {
    return null;
  }

  return (
    <CiroExpandableCard
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <CuratedSearchCardHeader
          headerText={isRemixStep ? "Improve search" : "Search strategy"}
          headerIcon={<SparklesIcon className="h-6 w-6 text-orange-500" />}
        />
      }
      collapsedContent={
        <div>
          <CuratedSearchFiltersHeaderDisplay
            savedFilters={savedFilters as InternalCuratedSearchFilters}
          />
        </div>
      }
    >
      <div>
        <div className={classNames("text-xs", "text-gray-500", "mb-4", "ml-7")}>
          {!allJobsAreComplete && "This step can take up to 60 seconds"}
        </div>

        {jobs.map((job) => {
          const allJobSteps = processedSteps.filter(
            (stepAndBeforeShowingStrategy) => {
              const step = stepAndBeforeShowingStrategy.step;
              if (
                step.__typename === "AutopilotSearchDesignInitializationStep"
              ) {
                return false;
              }
              if ("jobId" in step) {
                return step.jobId === job.id;
              }
              return false;
            },
          );

          const afterShowingStrategySteps = allJobSteps.filter(
            (stepAndBeforeShowingStrategy) => {
              return !stepAndBeforeShowingStrategy.beforeShowingStrategy;
            },
          );

          const beforeShowingStrategyTextSteps = allJobSteps.filter(
            (stepAndBeforeShowingStrategy) => {
              return (
                stepAndBeforeShowingStrategy.beforeShowingStrategy &&
                stepAndBeforeShowingStrategy.step.__typename ===
                  "AutopilotSearchDesignTextOnlyStep"
              );
            },
          );

          const lastBeforeShowingStrategyTextStepArray = [
            beforeShowingStrategyTextSteps[
              beforeShowingStrategyTextSteps.length - 1
            ],
          ].filter(Boolean);

          const jobSteps = (
            afterShowingStrategySteps.length
              ? afterShowingStrategySteps
              : lastBeforeShowingStrategyTextStepArray
          ).map((s) => s.step);

          if (jobSteps.length === 0) return null;

          const hasBestStrategy = jobSteps.some(
            (step) =>
              step.__typename === "AutopilotSearchDesignInternalFiltersStep" &&
              step.internalFiltersData?.isBestStrategy,
          );

          return (
            <CuratedSearchDesignJobGroup
              key={job.id}
              job={job}
              steps={jobSteps}
              allJobsAreComplete={allJobsAreComplete}
              isBestStrategy={hasBestStrategy}
              isSelected={selectedStrategy === job.id}
              onSelect={
                allJobsAreComplete
                  ? () => {
                      setSelectedStrategy(
                        job.id as AutopilotSearchStrategyJobEnum,
                      );
                      setSavedFilters(
                        allStrategies[job.id].internalFiltersData.filters as T,
                      );
                    }
                  : undefined
              }
            >
              {jobSteps.map((step, idx, arr) => {
                // Don't render internal filters steps inside the job group
                if (step.__typename !== "AutopilotSearchDesignTextOnlyStep") {
                  return null;
                }
                const isCurrentStep = idx === arr.length - 1;
                return (
                  <CuratedSearchDesignTextOnlyStep
                    key={idx}
                    data={getFragmentData(
                      CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
                      makeFragmentData(
                        step,
                        CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
                      ),
                    )}
                    isCurrentStep={isCurrentStep && !allJobsAreComplete}
                  />
                );
              })}
            </CuratedSearchDesignJobGroup>
          );
        })}

        {isLsn && (
          <CuratedSearchDesignLsnSteps
            processedSteps={processedSteps.map(
              (stepAndBeforeShowingStrategy) =>
                stepAndBeforeShowingStrategy.step,
            )}
            isComplete={allJobsAreComplete}
          />
        )}

        {allJobsAreComplete && savedFilters && (
          <div ref={bestStrategyRef}>
            {!isLsn && (
              <CuratedSearchDesignSelectedStrategyCard
                selectedStrategy={selectedStrategy}
                showRecommendedBadge={
                  Object.keys(allStrategies).length > 1 &&
                  selectedStrategy === bestStrategyFilterId
                }
                savedFilters={savedFilters as InternalCuratedSearchFilters}
                setSavedFilters={
                  setSavedFilters as (
                    value: InternalCuratedSearchFilters | null,
                  ) => void
                }
              />
            )}
            {isLsn && <CuratedLinkedinSalesNavigatorSearchFiltersCard />}
          </div>
        )}
        {hasError && (
          <div className={classNames("text-gray-500", "mt-2")}>
            There was an error processing your request. Please try again. Or
            contact{" "}
            <a
              className={classNames("text-red-500")}
              href="mailto:support@ciro.io"
            >
              support@ciro.io
            </a>{" "}
            if the issue persists.
          </div>
        )}
      </div>
    </CiroExpandableCard>
  );
};

export default CuratedSearchDesignStepsContainer;
